import React, { forwardRef, useState, useImperativeHandle, useRef } from 'react';
import { tr } from '../../../i18n/i18n';
import { useTranslation } from 'react-i18next';
import { ModalHandle } from '../';
import ConfirmationModal from '../ConfirmationModal';
import { AppButton, AppTextarea } from '../../atoms';

interface DeleteInvoiceModalProps {
  // Pass the selected assign_invoice_id so modal can include it in the delete call
  assignInvoiceId?: string | null;
  onDelete: (assign_invoice_id: string, delete_reason: string) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

const DeleteInvoiceModal = forwardRef<ModalHandle, DeleteInvoiceModalProps>(
  ({ assignInvoiceId, onDelete, onCancel, isLoading = false }, ref) => {
    const { t } = useTranslation();
    const modalRef = useRef<ModalHandle>(null);
    const [deletionReason, setDeletionReason] = useState('');
    const [error, setError] = useState('');

    // Expose modal methods to parent
    useImperativeHandle(ref, () => ({
      openModal: () => {
        setDeletionReason('');
        setError('');
        modalRef.current?.openModal();
      },
      closeModal: () => {
        setDeletionReason('');
        setError('');
        modalRef.current?.closeModal();
      },
    }));

    const handleDelete = () => {
      // Validate deletion reason (required, max 500 characters)
      const trimmed = deletionReason.trim();
      if (!trimmed) {
        setError(tr('g.deletion_reason_is_mandatory', 'Deletion reason is mandatory'));
        return;
      }

      if (trimmed.length < 5) {
        setError(tr('g.deletion_reason_must_be_at_least_5_characters', 'Deletion reason must be at least 5 characters'));
        return;
      }

      if (trimmed.length > 150) {
        setError(tr('g.deletion_reason_cannot_exceed_150_characters', 'Deletion reason cannot exceed 150 characters'));
        return;
      }

      onDelete(assignInvoiceId || '', deletionReason.trim());
    };

    const handleCancel = () => {
      setDeletionReason('');
      setError('');
      onCancel();
    };

    const handleReasonChange = (value: string) => {
      setDeletionReason(value);
      if (error) setError(''); // Clear error on input change
    };

    // Build title with invoice id if available (avoid duplicate #)
    const displayInvoice = assignInvoiceId
      ? assignInvoiceId.startsWith('#')
        ? assignInvoiceId
        : `#${assignInvoiceId}`
      : '';

    const title = `Delete Invoice ${displayInvoice || ''}${displayInvoice ? '?' : ''}`;

    const trimmedLength = deletionReason.trim().length;

    const content = (
      <div className="flex flex-col items-center justify-center gap-4 p-4">
        {/* Description */}
        <div className="text-sm font-normal text-center font-poppins text-gray-600">
          Are you sure you want to delete this invoice? This action cannot be undone. Please provide
          a reason for this deletion.
        </div>

        {/* Textarea for Deletion Reason */}
        <div className="w-full">
          <AppTextarea
            value={deletionReason}
            onChange={(e) => handleReasonChange(e.target.value)}
            placeholder={t('common.placeholder_enter_deletion_reason', 'Enter reason for deletion (required, max 150 characters)')}
            rows={4}
            maxLength={150}
            className={`w-full ${error ? 'border-red-500' : ''}`}
            disabled={isLoading}
          />
          {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
          {trimmedLength < 5 && (
            <p className="text-gray-500 text-xs mt-1">
              Enter at least {5 - trimmedLength} more character{trimmedLength === 4 ? '' : 's'} to
              enable deletion.
            </p>
          )}
          <p className="text-gray-500 text-xs mt-1 text-right">
            {deletionReason.length}/150 characters
          </p>
        </div>
      </div>
    );

    return (
      <ConfirmationModal
        ref={modalRef}
        title={title}
        content={content}
        icon="pi-trash"
        width="500px"
        severity="danger"
        buttonSeverity="danger"
        yesLabel={tr('fe_modals.yes_delete_invoice', "Yes, Delete Invoice")}
        noLabel={t('common.button_cancel')}
        yesDisabled={isLoading || deletionReason.trim().length < 5}
        onYes={handleDelete}
        onNo={handleCancel}
      />
    );
  },
);

DeleteInvoiceModal.displayName = 'DeleteInvoiceModal';

export default DeleteInvoiceModal;
